function crc32(data) {
  let crc = 0xffffffff
  for (let i = 0; i < data.length; i++) {
    crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8)
  }
  return (crc ^ 0xffffffff) >>> 0
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let j = 0; j < 8; j++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[i] = c >>> 0
  }
  return table
})()

function concatBytes(parts) {
  const length = parts.reduce((sum, part) => sum + part.length, 0)
  const out = new Uint8Array(length)
  let offset = 0
  parts.forEach((part) => {
    out.set(part, offset)
    offset += part.length
  })
  return out
}

function u16(value) {
  const bytes = new Uint8Array(2)
  new DataView(bytes.buffer).setUint16(0, value, true)
  return bytes
}

function u32(value) {
  const bytes = new Uint8Array(4)
  new DataView(bytes.buffer).setUint32(0, value >>> 0, true)
  return bytes
}

function zipStore(files) {
  const encoder = new TextEncoder()
  const locals = []
  const centrals = []
  let offset = 0

  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name)
    const data = file.data
    const crc = crc32(data)
    const local = concatBytes([
      new Uint8Array([0x50, 0x4b, 0x03, 0x04]),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
      data
    ])
    const central = concatBytes([
      new Uint8Array([0x50, 0x4b, 0x01, 0x02]),
      u16(20),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(data.length),
      u32(data.length),
      u16(nameBytes.length),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(0),
      u32(offset),
      nameBytes
    ])
    locals.push(local)
    centrals.push(central)
    offset += local.length
  })

  const centralDir = concatBytes(centrals)
  const eocd = concatBytes([
    new Uint8Array([0x50, 0x4b, 0x05, 0x06]),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(centralDir.length),
    u32(offset),
    u16(0)
  ])
  return concatBytes([...locals, centralDir, eocd])
}

function escapeXml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function colLetter(index) {
  let n = index
  let result = ''
  while (n >= 0) {
    result = String.fromCharCode((n % 26) + 65) + result
    n = Math.floor(n / 26) - 1
  }
  return result
}

function sheetToXml(header, rows) {
  const allRows = [header, ...rows]
  const xmlRows = allRows
    .map((row, rowIndex) => {
      const cells = row
        .map((cell, colIndex) => {
          const ref = `${colLetter(colIndex)}${rowIndex + 1}`
          return `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(cell)}</t></is></c>`
        })
        .join('')
      return `<row r="${rowIndex + 1}">${cells}</row>`
    })
    .join('')

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${xmlRows}</sheetData></worksheet>`
}

function toUint8(text) {
  return new TextEncoder().encode(text)
}

export function downloadExcel(filename, sheets) {
  const safeSheets = sheets.filter((sheet) => sheet && sheet.header)
  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    ${safeSheets
      .map(
        (sheet, index) =>
          `<sheet name="${escapeXml(sheet.name || `Sheet${index + 1}`)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`
      )
      .join('')}
  </sheets>
</workbook>`

  const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${safeSheets
    .map(
      (_, index) =>
        `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`
    )
    .join('')}
</Relationships>`

  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package-relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  ${safeSheets
    .map(
      (_, index) =>
        `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
    )
    .join('')}
</Types>`

  const rootRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`

  const files = [
    { name: '[Content_Types].xml', data: toUint8(contentTypesXml) },
    { name: '_rels/.rels', data: toUint8(rootRelsXml) },
    { name: 'xl/workbook.xml', data: toUint8(workbookXml) },
    { name: 'xl/_rels/workbook.xml.rels', data: toUint8(relsXml) },
    ...safeSheets.map((sheet, index) => ({
      name: `xl/worksheets/sheet${index + 1}.xml`,
      data: toUint8(sheetToXml(sheet.header, sheet.rows || []))
    }))
  ]

  const zipBytes = zipStore(files)
  const blob = new Blob([zipBytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const downloadName = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`
  saveBlob(blob, downloadName)
}

export function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

function decodeXml(value) {
  return String(value || '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
}

function parseCsv(text) {
  const rows = []
  let current = ''
  let row = []
  let inQuotes = false

  const input = text.replace(/^\uFEFF/, '')
  for (let i = 0; i < input.length; i++) {
    const char = input[i]
    const next = input[i + 1]
    if (inQuotes) {
      if (char === '"' && next === '"') {
        current += '"'
        i++
      } else if (char === '"') {
        inQuotes = false
      } else {
        current += char
      }
    } else if (char === '"') {
      inQuotes = true
    } else if (char === ',') {
      row.push(current)
      current = ''
    } else if (char === '\n') {
      row.push(current)
      rows.push(row)
      row = []
      current = ''
    } else if (char !== '\r') {
      current += char
    }
  }
  if (current !== '' || row.length) {
    row.push(current)
    rows.push(row)
  }
  return rows.filter((item) => item.some((cell) => String(cell).trim() !== ''))
}

function parseHtmlTable(text) {
  const doc = new DOMParser().parseFromString(text, 'text/html')
  const table = doc.querySelector('table')
  if (!table) return []
  return Array.from(table.rows).map((tr) =>
    Array.from(tr.cells).map((td) => td.textContent.trim())
  )
}

function parseSpreadsheetMl(text) {
  const doc = new DOMParser().parseFromString(text, 'text/xml')
  const rows = Array.from(doc.getElementsByTagName('Row'))
  return rows.map((row) =>
    Array.from(row.getElementsByTagName('Cell')).map((cell) => {
      const data = cell.getElementsByTagName('Data')[0]
      return data ? data.textContent.trim() : ''
    })
  )
}

async function inflateRaw(data) {
  const InflateStream = typeof window !== 'undefined' ? window.DecompressionStream : null
  if (!InflateStream) {
    throw new Error('当前浏览器无法解析压缩后的 xlsx，请另存为 CSV 后导入')
  }
  const stream = new Blob([data]).stream().pipeThrough(new InflateStream('deflate-raw'))
  const buffer = await new Response(stream).arrayBuffer()
  return new Uint8Array(buffer)
}

async function unzip(arrayBuffer) {
  const view = new DataView(arrayBuffer)
  const bytes = new Uint8Array(arrayBuffer)
  const files = {}
  let offset = 0

  while (offset + 30 <= bytes.length) {
    const signature = view.getUint32(offset, true)
    if (signature !== 0x04034b50) break

    const flags = view.getUint16(offset + 6, true)
    const method = view.getUint16(offset + 8, true)
    let compressedSize = view.getUint32(offset + 18, true)
    const nameLength = view.getUint16(offset + 26, true)
    const extraLength = view.getUint16(offset + 28, true)
    const name = new TextDecoder().decode(bytes.slice(offset + 30, offset + 30 + nameLength))
    let dataStart = offset + 30 + nameLength + extraLength
    let fileData

    if (flags & 0x0008) {
      throw new Error('不支持该 Excel 压缩格式，请另存为 CSV 后导入')
    }

    fileData = bytes.slice(dataStart, dataStart + compressedSize)
    if (method === 0) {
      files[name] = fileData
    } else if (method === 8) {
      files[name] = await inflateRaw(fileData)
    } else {
      throw new Error('不支持的 Excel 压缩方式，请另存为 CSV 后导入')
    }
    offset = dataStart + compressedSize
  }

  return files
}

function parseSheetXml(xml, sharedStrings) {
  const rows = []
  const rowMatches = xml.match(/<row\b[\s\S]*?<\/row>/g) || []
  rowMatches.forEach((rowXml) => {
    const cells = []
    const cellMatches = rowXml.match(/<c\b[\s\S]*?<\/c>|<c\b[^>]*\/>/g) || []
    cellMatches.forEach((cellXml) => {
      const refMatch = cellXml.match(/r="([A-Z]+)\d+"/)
      const col = refMatch ? refMatch[1].split('').reduce((n, ch) => n * 26 + (ch.charCodeAt(0) - 64), 0) - 1 : cells.length
      const typeMatch = cellXml.match(/t="([^"]+)"/)
      const type = typeMatch ? typeMatch[1] : ''
      let value = ''
      if (type === 'inlineStr') {
        const textMatch = cellXml.match(/<t[^>]*>([\s\S]*?)<\/t>/)
        value = textMatch ? decodeXml(textMatch[1]) : ''
      } else {
        const valueMatch = cellXml.match(/<v[^>]*>([\s\S]*?)<\/v>/)
        value = valueMatch ? decodeXml(valueMatch[1]) : ''
        if (type === 's') {
          value = sharedStrings[Number(value)] || ''
        }
      }
      cells[col] = value
    })
    for (let i = 0; i < cells.length; i++) {
      if (cells[i] == null) cells[i] = ''
    }
    rows.push(cells)
  })
  return rows
}

function parseSharedStrings(xml) {
  if (!xml) return []
  const items = xml.match(/<si>[\s\S]*?<\/si>/g) || []
  return items.map((item) => {
    const texts = []
    const regex = /<t[^>]*>([\s\S]*?)<\/t>/g
    let match
    while ((match = regex.exec(item))) {
      texts.push(decodeXml(match[1]))
    }
    return texts.join('')
  })
}

async function parseXlsx(arrayBuffer) {
  const files = await unzip(arrayBuffer)
  const decoder = new TextDecoder()
  const sharedXml = files['xl/sharedStrings.xml']
    ? decoder.decode(files['xl/sharedStrings.xml'])
    : ''
  const sharedStrings = parseSharedStrings(sharedXml)
  const sheetFile = Object.keys(files).find((name) =>
    /xl\/worksheets\/sheet1\.xml$/.test(name)
  )
  if (!sheetFile) {
    throw new Error('Excel 文件中没有找到工作表')
  }
  return parseSheetXml(decoder.decode(files[sheetFile]), sharedStrings)
}

function rowsToObjects(rows) {
  if (!rows.length) return []
  const header = rows[0].map((item) => String(item || '').trim())
  return rows
    .slice(1)
    .filter((row) => row.some((cell) => String(cell || '').trim() !== ''))
    .map((row) => {
      const item = {}
      header.forEach((key, index) => {
        if (key) item[key] = row[index] == null ? '' : String(row[index]).trim()
      })
      return item
    })
}

export function parseTableFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('文件读取失败'))
    reader.onload = async () => {
      try {
        const buffer = reader.result
        const bytes = new Uint8Array(buffer)
        if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
          resolve(rowsToObjects(await parseXlsx(buffer)))
          return
        }
        const text = new TextDecoder('utf-8').decode(bytes).replace(/^\uFEFF/, '')
        if (/<Worksheet[\s>]|<ss:Workbook|<Workbook[\s>]/.test(text)) {
          resolve(rowsToObjects(parseSpreadsheetMl(text)))
          return
        }
        if (/<table[\s>]/i.test(text)) {
          resolve(rowsToObjects(parseHtmlTable(text)))
          return
        }
        resolve(rowsToObjects(parseCsv(text)))
      } catch (error) {
        reject(error)
      }
    }
    reader.readAsArrayBuffer(file)
  })
}
